import React, { useState, useEffect } from 'react';
import { TrophyIcon, MedalIcon, CrownIcon } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { Generation } from '../types';

interface Winner extends Generation {
    votes_count: number;
    challenge_title: string;
}

export const HallOfFame: React.FC = () => {
    const [winners, setWinners] = useState<Winner[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchWinners();
    }, []);

    const fetchWinners = async () => {
        try {
            // 1. Get the most recently finished challenge
            const { data: lastChallenge } = await supabase
                .from('weekly_challenges')
                .select('*')
                .eq('is_active', false)
                .order('end_date', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (!lastChallenge) {
                setLoading(false);
                return;
            }

            // 2. Get top 3 submissions for this challenge
            const { data: submissions } = await supabase
                .from('challenge_submissions')
                .select(`
                    votes_count,
                    generation:generations (
                        *,
                        user:profiles (
                            username,
                            avatar_url
                        )
                    )
                `)
                .eq('challenge_id', lastChallenge.id)
                .order('votes_count', { ascending: false })
                .limit(3);

            if (submissions) {
                const formattedWinners = submissions.map((sub: any) => ({
                    ...sub.generation,
                    votes_count: sub.votes_count,
                    challenge_title: lastChallenge.title,
                    user: Array.isArray(sub.generation.user) ? sub.generation.user[0] : sub.generation.user
                }));
                setWinners(formattedWinners);
            }
        } catch (err) {
            console.error('Error fetching Hall of Fame:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading || winners.length === 0) return null;

    return (
        <div className="mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
            <div className="flex items-center gap-3 mb-6">
                <TrophyIcon className="w-6 h-6 text-yellow-500" />
                <h2 className="text-2xl font-bold text-white tracking-tight">Hall of Fame</h2>
                <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {winners.map((winner, index) => (
                    <div key={winner.id} className="relative group perspective-1000">
                        {/* Rank Badge */}
                        <div className="absolute -top-4 -left-4 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-onyx border-2 border-white/10 shadow-xl">
                            {index === 0 && <CrownIcon className="w-6 h-6 text-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]" />}
                            {index === 1 && <MedalIcon className="w-6 h-6 text-zinc-300" />}
                            {index === 2 && <MedalIcon className="w-6 h-6 text-amber-700" />}
                        </div>

                        <div className="relative aspect-[16/9] rounded-xl overflow-hidden border border-white/10 bg-zinc-900 shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]">
                            <img
                                src={winner.img}
                                alt={winner.prompt}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80" />

                            <div className="absolute bottom-0 left-0 w-full p-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-full border border-white/20 overflow-hidden bg-zinc-800">
                                        {winner.user?.avatar_url && <img src={winner.user.avatar_url} className="w-full h-full object-cover" />}
                                    </div>
                                    <div>
                                        <p className="text-white font-bold text-sm">@{winner.user?.username || 'Creator'}</p>
                                        <p className="text-zinc-400 text-xs">{winner.votes_count} Votes</p>
                                    </div>
                                </div>
                                <p className="text-yellow-500/80 text-[10px] font-mono uppercase tracking-widest border-t border-white/10 pt-2 mt-2">
                                    Winner: {winner.challenge_title}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
